import { type CalendarEventData, EventCategory, EventCategoryIconMap, EventCategoryMap } from "@qendo/calendar";
import { OptionType } from "@qendo/types";
import { endOfDay, isSameMinute, startOfDay } from "date-fns";
import { Link } from "expo-router";
import { useMemo } from "react";
import { Pressable, View } from "react-native";
import { StatusTags } from "~/components/layout/calendar/StatusTags";
import { ThumbnailGallery } from "~/components/layout/gallery/ThumbnailGallery";
import { JournalLine } from "~/components/layout/journal/JournalLine";
import { Subtitle, Title } from "~/components/layout/text";
import { Can } from "~/components/permission/Can";
import { QendoIcon } from "~/components/QendoIcon";
import { Flex, Gap, Items, Margin, Padding, Rounded, Width } from "~/constants/layout";
import { useJournal } from "~/providers/JournalProvider";
import { useOptions } from "~/providers/OptionsProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";
import { currentTimezone, formatTimeForHumans, formatTimezoneName, fromTimezone, timezonesMatch } from "~/util/dates";

type EventListingProps = {
    event: CalendarEventData;
};

export const EventListing = ({ event }: EventListingProps) => {
    const { colours, background } = useTheme();
    const { findOption } = useOptions();
    const { findEvent } = useJournal();
    const current_timezone = currentTimezone();
    const time_zone = event.time_zone ?? current_timezone;
    const is_same_timezone = useMemo(() => timezonesMatch(current_timezone, time_zone), [current_timezone, time_zone]);

    const event_start = fromTimezone(event.start_at, time_zone);
    const event_end = filled(event.end_at) ? fromTimezone(event.end_at, time_zone) : null;

    const start_at = isSameMinute(event_start, startOfDay(event_start)) ? "Midnight" : formatTimeForHumans(event_start);
    const end_at = event_end
        ? isSameMinute(event_end, endOfDay(event_end))
            ? "Midnight"
            : formatTimeForHumans(event_end)
        : null;

    const real_event = useMemo(() => (event.primary_id && findEvent(event.primary_id)) || event, [event, findEvent]);

    const option = useMemo(
        () => findOption(OptionType.calendar_events, real_event.option_id),
        [findOption, real_event.option_id],
    );

    const event_type = option?.group ?? EventCategory.appointment;
    const title = option?.title ?? EventCategoryMap[event_type];
    const icon = EventCategoryIconMap[event_type];

    return (
        <Link href={{ pathname: "/calendar/[id]/show", params: { id: event.id } }} asChild>
            <Pressable>
                <View style={[Flex.row, Items.start, Gap.sm, Rounded.lg, Padding.sm, Width.full, background.primary]}>
                    <QendoIcon name={icon} size={24} color={colours.icon.secondary} />
                    <View style={[Gap.xxs, Flex.flex, Items.start]}>
                        <View style={[Flex.row, Flex.between, Items.center, Width.full]}>
                            <Title size="base">{title}</Title>
                            <Subtitle align="right" style={Flex.shrink_0}>
                                {EventCategoryMap[event_type]}
                            </Subtitle>
                        </View>
                        <StatusTags event={real_event} />
                        <JournalLine data={real_event.title} />
                        <JournalLine title="Time">{[start_at, end_at].filter(filled).join(" - ")}</JournalLine>
                        {is_same_timezone || <JournalLine title="Time zone" data={formatTimezoneName(time_zone)} />}
                        {filled(real_event.location) && <JournalLine title="Location" data={real_event.location} />}
                        <View style={[Items.start, Gap.xs, Margin.top_xxs]}>
                            <Can ability="view" section="photos">
                                <ThumbnailGallery images={real_event.photograph_ids} />
                            </Can>
                        </View>
                    </View>
                </View>
            </Pressable>
        </Link>
    );
};
